import { inject } from '@angular/core';
import { CanActivateFn, Route, Router, Routes } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { BrightnavShellComponent } from '../layout/shell/brightnav-shell.component';
import { authGroupGuard, authGuard } from '@brightside/core/auth';
import { AmplitudeService } from '@brighthub/logging';
import { BrightNavAmplitudeService } from '@brighthub/brightnav-logging';
import { BrightNavOmniSearchRegistry } from '@brighthub/brightnav-registry';
import { ConversationLayoutComponent } from '../layout/conversation-layout/conversation-layout.component';
import { MyClientsRouteComponent } from '../layout/my-clients-route/my-clients-route.component';
import { EmployeeLayoutComponent } from '../layout/employee-layout/employee-layout.component';
import {
  BRIGHTNAV_ADMIN_DEVELOPER_OKTA_GROUP,
  BRIGHTNAV_ADMIN_OKTA_GROUP,
} from '@brighthub/admin-contract';
import {
  featureFlagGuard,
  FeatureFlagKey,
  FeatureFlagsService,
} from '@brighthub/feature-flags';
import { DashboardPageComponent } from '@brightnav/dashboard';

function isHomeEnabled(value: unknown): boolean {
  return value !== false && value !== null && value !== undefined;
}

const homeFlagGuard = featureFlagGuard(FeatureFlagKey.ENABLEHOMEPAGE, {
  redirectTo: '/brightnav/myclients',
});

/**
 * Client deep links open My Clients; other visits follow the Home feature flag.
 * `redirectTo` cannot share a route with `canMatch` or `canActivate` (NG04014).
 */
const brightnavIndexGuard: CanActivateFn = (route) => {
  const flags = inject(FeatureFlagsService);
  const router = inject(Router);
  const extras = {
    queryParams: route.queryParams,
    fragment: route.fragment ?? undefined,
  };
  if (route.queryParamMap.get('guid')?.trim()) {
    return router.createUrlTree(['/brightnav/myclients'], extras);
  }
  return firstValueFrom(
    flags.getValue$(FeatureFlagKey.ENABLEHOMEPAGE, false),
  ).then((value) =>
    router.createUrlTree(
      [isHomeEnabled(value) ? '/brightnav/home' : '/brightnav/myclients'],
      extras,
    ),
  );
};

/** Shared children under the canonical `/brightnav` shell. */
const brightnavChildren: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [brightnavIndexGuard],
    children: [],
  },
  {
    path: 'home',
    canMatch: [homeFlagGuard],
    component: DashboardPageComponent,
  },
  {
    path: 'myclients',
    component: MyClientsRouteComponent,
  },
  {
    path: 'conversation',
    component: ConversationLayoutComponent,
    canMatch: [authGroupGuard([BRIGHTNAV_ADMIN_OKTA_GROUP])],
  },
  {
    path: 'conversation/:conversationId',
    component: ConversationLayoutComponent,
    canMatch: [authGroupGuard([BRIGHTNAV_ADMIN_OKTA_GROUP])],
  },
  {
    path: 'employee',
    component: EmployeeLayoutComponent,
    canMatch: [authGroupGuard([BRIGHTNAV_ADMIN_OKTA_GROUP])],
  },
  {
    path: 'employee/:oktaId',
    component: EmployeeLayoutComponent,
    canMatch: [authGroupGuard([BRIGHTNAV_ADMIN_OKTA_GROUP])],
  },
  {
    path: 'admin',
    canMatch: [authGroupGuard([BRIGHTNAV_ADMIN_DEVELOPER_OKTA_GROUP])],
    loadChildren: () =>
      import('@brighthub/brightnav-admin').then(
        (m) => m.brightnavAdminFeatureRoutes,
      ),
  },
];

function brightnavShellRoute(path: string): Route {
  return {
    path,
    component: BrightnavShellComponent,
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    providers: [
      { provide: AmplitudeService, useClass: BrightNavAmplitudeService },
      BrightNavOmniSearchRegistry,
    ],
    children: brightnavChildren,
  };
}

/** Conversation / employee tools use the broader admin group; the Administration SPA uses {@link BRIGHTNAV_ADMIN_DEVELOPER_OKTA_GROUP}. */
export const BrightnavRoutes: Routes = [
  brightnavShellRoute('brightnav'),
  /**
   * @deprecated Legacy URL prefix — redirect bookmarks/deep links onto canonical `/brightnav/**`.
   * `pathMatch: 'prefix'` preserves the remainder (`/brightai/myclients` → `/brightnav/myclients`).
   */
  {
    path: 'brightai',
    redirectTo: 'brightnav',
    pathMatch: 'prefix',
  },
];
